import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_API_URL;

const PHOTO_CATEGORIES = [
  { value: 'finca', label: 'bmp_cat_farm' },
  { value: 'empacadora', label: 'bmp_cat_packing' },
  { value: 'fruta', label: 'bmp_cat_fruit' },
  { value: 'proceso', label: 'bmp_cat_process' },
  { value: 'otro', label: 'bmp_cat_other' },
];

export default function FarmFormModal({ farm, onClose, onSave, saving }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: farm?.name || '',
    zone: farm?.zone || 'norte',
    city: farm?.city || '',
    hectares: farm?.hectares || '',
    fruit_type: farm?.fruit_type || 'convencional',
    certifications: farm?.certifications || '',
  });

  // Photos state (only when editing)
  const [photos, setPhotos] = useState([]);
  const [coverUploading, setCoverUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoCategory, setPhotoCategory] = useState('finca');

  useEffect(() => {
    if (farm?.id) {
      axios.get(`${API}/market/farms/${farm.id}/photos/`)
        .then(res => setPhotos(res.data))
        .catch(() => {});
    }
  }, [farm]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !farm?.id) return;
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      await axios.post(`${API}/market/farms/${farm.id}/cover/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (err) {
      alert(t('bmp_upload_error'));
    } finally {
      setCoverUploading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !farm?.id) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('category', photoCategory);
      const res = await axios.post(`${API}/market/farms/${farm.id}/photos/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotos(prev => [...prev, res.data]);
    } catch (err) {
      alert(t('bmp_upload_error'));
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm(t('bmp_confirm_delete_photo'))) return;
    try {
      await axios.delete(`${API}/market/photos/${photoId}/`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch { alert('Error'); }
  };

  return (
    <div className="bmp-modal-overlay" onClick={onClose}>
      <div className="bmp-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
        <div className="bmp-modal-header">
          <h3>{farm ? t('bmp_edit_farm') : t('bmp_add_farm')}</h3>
          <button className="bmp-modal-close" onClick={onClose}>&#10005;</button>
        </div>
        <form onSubmit={handleSubmit} className="bmp-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="bmp-form-row">
            <div className="bmp-field">
              <label>{t('bmp_farm_name')}</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder={t('bmp_farm_name_placeholder')} />
            </div>
            <div className="bmp-field">
              <label>{t('mkt_zone')}</label>
              <select value={form.zone} onChange={e => setForm(f => ({ ...f, zone: e.target.value }))}>
                <option value="norte">{t('mkt_zone_north')}</option>
                <option value="sur">{t('mkt_zone_south')}</option>
              </select>
            </div>
            <div className="bmp-field">
              <label>{t('mkt_city')}</label>
              <input type="text" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder={t('mkt_city_placeholder')} />
            </div>
          </div>
          <div className="bmp-form-row">
            <div className="bmp-field">
              <label>{t('bmp_hectares')}</label>
              <input type="number" step="0.01" min="0" value={form.hectares} onChange={e => setForm(f => ({ ...f, hectares: e.target.value }))} placeholder="0" />
            </div>
            <div className="bmp-field">
              <label>{t('mkt_fruit_type')}</label>
              <select value={form.fruit_type} onChange={e => setForm(f => ({ ...f, fruit_type: e.target.value }))}>
                <option value="convencional">{t('mkt_conventional')}</option>
                <option value="organico">{t('mkt_organic')}</option>
                <option value="ambos">{t('mkt_both_fruit')}</option>
              </select>
            </div>
            <div className="bmp-field">
              <label>{t('bmp_certifications')}</label>
              <input type="text" value={form.certifications} onChange={e => setForm(f => ({ ...f, certifications: e.target.value }))} placeholder={t('bmp_certifications_placeholder')} />
            </div>
          </div>

          {/* Cover photo — only when editing */}
          {farm?.id && (
            <div className="bmp-field">
              <label>{t('bmp_farm_cover')}</label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {farm.cover_url && <img src={farm.cover_url} alt="" style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 6 }} />}
                <label className="bmp-btn bmp-btn-secondary" style={{ cursor: 'pointer' }}>
                  {coverUploading ? t('bmp_uploading') : t('bmp_upload_cover')}
                  <input type="file" accept="image/*" onChange={handleCoverUpload} hidden disabled={coverUploading} />
                </label>
              </div>
            </div>
          )}

          {/* Gallery — only when editing */}
          {farm?.id && (
            <div className="bmp-field">
              <label>{t('bmp_farm_gallery')}</label>
              {photos.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  {photos.map(p => (
                    <div key={p.id} style={{ position: 'relative', width: 80, height: 60 }}>
                      <img src={p.image_url} alt={p.caption} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                      <button
                        type="button"
                        onClick={() => handleDeletePhoto(p.id)}
                        style={{ position: 'absolute', top: -4, right: -4, background: 'var(--bmp-danger)', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, fontSize: 10, cursor: 'pointer', lineHeight: '18px', padding: 0 }}
                      >&#10005;</button>
                      <span style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '1px 4px', borderRadius: 3 }}>{p.category_display}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select value={photoCategory} onChange={e => setPhotoCategory(e.target.value)} style={{ padding: '6px 10px', border: '1px solid var(--bmp-border)', borderRadius: 6, fontSize: 12 }}>
                  {PHOTO_CATEGORIES.map(c => <option key={c.value} value={c.value}>{t(c.label)}</option>)}
                </select>
                <label className="bmp-btn bmp-btn-secondary" style={{ cursor: 'pointer' }}>
                  {photoUploading ? t('bmp_uploading') : t('bmp_add_photo')}
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} hidden disabled={photoUploading} />
                </label>
              </div>
            </div>
          )}

          <div className="bmp-form-actions">
            <button type="button" className="bmp-btn bmp-btn-secondary" onClick={onClose}>{t('cancel')}</button>
            <button type="submit" className="bmp-btn bmp-btn-primary" disabled={saving}>{saving ? t('saving') : t('wc_save')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
